import React, { useEffect, useState } from "react";
import { useStore } from "react-redux";
import { Category, CloudUpload, ExpandLess, ExpandMore, Polyline } from "@mui/icons-material";
import { Box, Button } from "@mui/material";

import { NavigationButton } from "ui";

import ImportDataProgress from "fond/project/panels/polygon/ImportDataProgress";
import PolygonSelectBody from "fond/project/panels/polygon/PolygonSelectBody";
import { usePolygonSelect } from "fond/project/panels/polygon/usePolygonSelect";
import { PolygonState, selectUploadOption } from "fond/project/polygon/redux";
import { closePolygonSelect } from "fond/project/redux";
import { Store } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";

import { selectIsBoundarySelectOpen } from "./boundary";
import BoundaryImportChecklist from "./BoundaryImportChecklist";
import { FlyoutSubOption, FlyoutSubOptions } from "./FlyoutSubOptions";
import ReplaceDataConfirmation from "./ReplaceDataConfirmation";
import { useBaseDataDraw } from "./useBaseDataDraw";
import { useBeginBoundarySelect } from "./useBoundary";

const OPTION_BUTTON_SX = { m: 0 };

/** Whether the boundary option is collapsed, confirming the existing boundary's replacement, or offering ways to add one. */
type OptionStep = "collapsed" | "confirming" | "choosing";

interface BoundarySectionProps {
  /** Whether the version already has a boundary. */
  hasBoundary: boolean;
  /** Called once data inside the boundary is imported, or the boundary is saved without importing. */
  onComplete(): void;
}

/**
 * An option for drawing or uploading the boundary, followed by a checklist of FOND data to import from inside it.
 */
const BoundarySection: React.FC<BoundarySectionProps> = ({ hasBoundary, onComplete }) => {
  const dispatch = useAppDispatch();
  const store = useStore<Store>();
  const { startDraw } = useBaseDataDraw();
  const beginBoundarySelect = useBeginBoundarySelect();
  const polygonSelect = usePolygonSelect();
  const { polygonState } = polygonSelect.polygon;
  const isActive = useAppSelector(selectIsBoundarySelectOpen);
  const [isImporting, setIsImporting] = useState(false);
  const [step, setStep] = useState<OptionStep>("collapsed");

  const showImportProgress = isImporting || (isActive && polygonState === PolygonState.upload);
  const isUploadingBoundary = isActive && (polygonState === PolygonState.uploadingPolygon || !!polygonSelect.upload);
  const isExpanded = step !== "collapsed" || isUploadingBoundary;

  // Leave polygon select when the section closes, unless the boundary is being drawn or its data imported.
  useEffect(
    () => () => {
      const state = store.getState();
      if (
        selectIsBoundarySelectOpen(state) &&
        state.baseDataDraw.drawingKey !== "boundary" &&
        state.project.polygon.polygonState !== PolygonState.upload
      ) {
        dispatch(closePolygonSelect());
      }
    },
    [dispatch, store]
  );

  const close = () => dispatch(closePolygonSelect());

  const handleOptionClick = () => {
    if (isExpanded) {
      if (isUploadingBoundary) close();
      setStep("collapsed");
    } else {
      setStep(hasBoundary ? "confirming" : "choosing");
    }
  };

  const handleUploadClick = () => {
    beginBoundarySelect();
    dispatch(selectUploadOption());
  };

  const renderExpanded = () => {
    if (isUploadingBoundary) {
      return (
        <Box>
          <PolygonSelectBody polygonSelect={polygonSelect} onImport={() => setIsImporting(true)} onCancel={close} />
          {!polygonSelect.upload && (
            <Box display="flex" justifyContent="flex-end" px={1} pt={1}>
              <Button size="small" onClick={close}>
                Cancel
              </Button>
            </Box>
          )}
        </Box>
      );
    }

    if (step === "confirming") {
      return (
        <ReplaceDataConfirmation
          message="You have already added a boundary. Proceeding will replace it."
          onCancel={() => setStep("collapsed")}
          onReplace={() => setStep("choosing")}
        />
      );
    }

    return (
      <FlyoutSubOptions>
        {hasBoundary && (
          <FlyoutSubOption
            icon={<Category />}
            label="Use existing boundary"
            description="Edit the existing boundary"
            onClick={() => startDraw("boundary")}
          />
        )}
        <FlyoutSubOption
          icon={<Polyline />}
          label="Draw area"
          description="Draw a polygon on the map"
          onClick={() => startDraw("boundary", { fromScratch: true })}
        />
        <FlyoutSubOption icon={<CloudUpload />} label="Upload area" description="Upload a polygon" onClick={handleUploadClick} />
      </FlyoutSubOptions>
    );
  };

  if (showImportProgress) {
    return (
      <ImportDataProgress
        title="Importing FOND data"
        onSuccess={() => {
          setIsImporting(false);
          onComplete();
        }}
        onCancel={() => setIsImporting(false)}
      />
    );
  }

  if (isActive && polygonState === PolygonState.complete) {
    return <BoundaryImportChecklist onImportStarted={() => setIsImporting(true)} onSkipped={onComplete} />;
  }

  return (
    <Box display="flex" flexDirection="column" gap={0.75}>
      <NavigationButton
        icon={<Polyline />}
        title={hasBoundary ? "Replace boundary" : "Add boundary"}
        subtitle="Draw or upload the design area"
        onClick={handleOptionClick}
        rightElement={isExpanded ? <ExpandLess color="action" /> : <ExpandMore color="action" />}
        sx={{ ...OPTION_BUTTON_SX, ...(isExpanded && { borderColor: "primary.light" }) }}
      />
      {isExpanded && renderExpanded()}
    </Box>
  );
};

export default BoundarySection;
